import { BusyContent, LoadingState } from './ui/Primitives';
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import './ObservabilityCenter.css'
import { getBackendHost } from '../utils/backendHost'
import CallLatencyPanel from './CallLatencyPanel'

const UI_BUILD = {
  version: process.env.REACT_APP_SAYABLE_RELEASE_VERSION || null,
  revision: process.env.REACT_APP_SAYABLE_GIT_REVISION || null,
}

const WINDOWS = [
  { value: '15m', label: '15 minutes' },
  { value: '1h', label: '1 hour' },
  { value: '24h', label: '24 hours' },
  { value: '7d', label: '7 days' }
]

const titleCase = (value) => String(value || 'unknown')
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/[-_]/g, ' ')
  .replace(/\b\w/g, letter => letter.toUpperCase())

const eventContext = (event) => Object.entries(event?.metadata || {})
  .filter(([, value]) => ['string', 'number', 'boolean'].includes(typeof value))
  .slice(0, 10)

const formatDate = (value) => {
  if (!value) return 'No signal yet'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? 'Unknown' : date.toLocaleString()
}

const formatDuration = (value) => {
  if (value === null || value === undefined) return '—'
  if (value >= 1000) return `${(value / 1000).toFixed(2)} s`
  return `${Math.round(value)} ms`
}

const formatBytes = (value) => {
  if (!Number.isFinite(Number(value))) return '—'
  const units = ['B', 'KB', 'MB', 'GB']
  let amount = Number(value)
  let unit = 0
  while (amount >= 1024 && unit < units.length - 1) {
    amount /= 1024
    unit += 1
  }
  return `${amount.toFixed(amount >= 10 ? 0 : 1)} ${units[unit]}`
}

const statusClass = (status) => ['healthy', 'ready', 'succeeded'].includes(status)
  ? 'healthy'
  : ['degraded', 'unknown', 'rejected', 'cancelled'].includes(status) ? 'attention' : 'danger'

const VIEW_COPY = {
  health: {
    title: 'Service health and performance',
    description: 'Cross-service readiness, latency, failures, and collection pressure.'
  },
  agents: {
    title: 'Agent runs and tool execution',
    description: 'Model requests, token usage, tool outcomes, and agent timing.'
  },
  calls: {
    title: 'Call performance',
    description: 'Caller to first AI audio timing, trends, and individual turns.'
  },
  logging: {
    title: 'Live logs and logging health',
    description: 'Search recent service output and inspect structured delivery, queues, and rotation.'
  },
  all: {
    title: 'Services, agent runs, tools, and logs',
    description: 'Cross-service operational signals with asynchronous collection and redacted event details.'
  }
}

function ObservabilityCenter({ view = 'all' }) {
  const backendHost = getBackendHost()
  const [data, setData] = useState(null)
  const [windowName, setWindowName] = useState('1h')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [logs, setLogs] = useState([])
  const [logSources, setLogSources] = useState({})
  const [logsLoading, setLogsLoading] = useState(false)
  const [logsLoaded, setLogsLoaded] = useState(false)
  const [logsError, setLogsError] = useState('')
  const [logService, setLogService] = useState('all')
  const [logLevel, setLogLevel] = useState('all')
  const [logSearch, setLogSearch] = useState('')
  const [logLimit, setLogLimit] = useState(200)
  const [expandedLog, setExpandedLog] = useState(null)

  const loadOverview = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true)
    if (!quiet) setError('')
    try {
      const token = localStorage.getItem('jwt_authorization')
      const response = await axios.get(
        `${backendHost}/v2/observability/overview`,
        {
          params: { window: windowName },
          headers: { Authorization: `Bearer ${token}` }
        }
      )
      setData(response.data)
    } catch (requestError) {
      const apiError = requestError.response?.data?.error
      setError(typeof apiError === 'string' ? apiError : apiError?.message || 'Observability data could not be loaded.')
    } finally {
      if (!quiet) setLoading(false)
    }
  }, [backendHost, windowName])

  useEffect(() => {
    loadOverview()
  }, [loadOverview])

  useEffect(() => {
    if (!['all', 'calls'].includes(view)) return undefined
    const refresh = () => {
      if (!document.hidden) loadOverview(true)
    }
    const timer = window.setInterval(refresh, 30000)
    return () => window.clearInterval(timer)
  }, [loadOverview, view])

  const loadLogs = useCallback(async () => {
    setLogsLoading(true)
    setLogsError('')
    try {
      const token = localStorage.getItem('jwt_authorization')
      const response = await axios.get(
        `${backendHost}/v2/observability/logs`,
        {
          params: {
            service: logService,
            level: logLevel,
            query: logSearch.trim(),
            limit: logLimit
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      )
      setLogs(Array.isArray(response.data?.logs) ? response.data.logs : [])
      setLogSources(response.data?.sources || {})
      setExpandedLog(null)
    } catch (requestError) {
      const apiError = requestError.response?.data?.error
      setLogsError(typeof apiError === 'string' ? apiError : apiError?.message || 'Live logs could not be loaded.')
    } finally {
      setLogsLoaded(true)
      setLogsLoading(false)
    }
  }, [backendHost, logLevel, logLimit, logSearch, logService])

  const filteredEvents = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return data?.recentEvents || []
    return (data?.recentEvents || []).filter(event => [
      event.service,
      event.name,
      event.status,
      event.traceId,
      event.runId,
      event.sessionId,
      event.toolName,
      ...eventContext(event).flatMap(([key, value]) => [key, value])
    ].some(value => String(value || '').toLowerCase().includes(query)))
  }, [data, search])

  const visibleLogs = useMemo(() => {
    const query = logSearch.trim().toLowerCase()
    return logs.filter(log => {
      if (logService !== 'all' && log.service !== logService) return false
      if (logLevel !== 'all' && String(log.level || '').toLowerCase() !== logLevel) return false
      if (!query) return true
      return JSON.stringify(log).toLowerCase().includes(query)
    })
  }, [logLevel, logSearch, logService, logs])

  const summary = data?.summary || {}
  const collection = data?.collection || {}
  const queue = collection.queue || {}
  const viewCopy = VIEW_COPY[view] || VIEW_COPY.all
  const showHealth = ['all', 'health'].includes(view)
  const showAgents = ['all', 'agents'].includes(view)
  const showCalls = ['all', 'calls'].includes(view)
  const showLogging = ['all', 'logging'].includes(view)

  useEffect(() => {
    if (view === 'logging' && !logsLoaded) loadLogs()
  }, [loadLogs, logsLoaded, view])

  const refreshCurrentView = () => {
    loadOverview()
    if (view === 'logging') loadLogs()
  }

  return (
    <section className="observability-center" aria-busy={loading}>
      <div className="observability-toolbar">
        <div>
          <span className="admin-section-kicker">Observability center</span>
          <h2>{viewCopy.title}</h2>
          <p>{viewCopy.description}</p>
        </div>
        <div className="observability-toolbar-actions">
          <label>
            <span>Window</span>
            <select value={windowName} onChange={event => setWindowName(event.target.value)}>
              {WINDOWS.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </label>
          <button type="button" className="admin-refresh-button" onClick={refreshCurrentView} disabled={loading || logsLoading}>
            <BusyContent busy={loading || logsLoading} label={"Refreshing…"}>{'Refresh'}</BusyContent>
          </button>
        </div>
      </div>

      {error ? <div className="admin-operations-alert" role="alert">{error}</div> : null}

      <div className="observability-summary-grid" hidden={!(showHealth || showAgents)}>
        <article><span>Agent runs</span><strong>{summary.agentRuns ?? '—'}</strong><small>{summary.traces || 0} correlated traces</small></article>
        <article><span>Tool calls</span><strong>{summary.toolCalls ?? '—'}</strong><small>All tool outcomes retained</small></article>
        <article className={summary.failures ? 'attention' : ''}><span>Failures</span><strong>{summary.failures ?? '—'}</strong><small>{summary.failureRate ?? 0}% of retained events</small></article>
        <article><span>p95 duration</span><strong>{formatDuration(summary.p95Ms)}</strong><small>Across timed retained events</small></article>
      </div>

      {showHealth && data?.scannerTraffic ? <details className="observability-card">
        <summary>Scanner traffic: {data.scannerTraffic.retainedCount} retained probes</summary>
        <p>Recognized probes against nonexistent routes. Excluded from application health metrics. Counts cover up to {data.scannerTraffic.queryLimit} retained probes in this window.</p>
        <ul>{(data.scannerTraffic.recentEvents || []).map(event => <li key={event.id}><time>{formatDate(event.timestamp)}</time> <code>{event.name}</code></li>)}</ul>
      </details> : null}

      <div className="observability-service-grid" hidden={!showHealth}>
        {(data?.services || []).map(item => {
          const service = item.service === 'ui' ? { ...item, build: UI_BUILD } : item
          return (
          <article className="observability-card" key={service.service}>
            <div className="observability-card-heading">
              <div><span className="service-dot" data-status={statusClass(service.status)} /><h3>{titleCase(service.service)}</h3></div>
              <span className={`operations-status ${statusClass(service.status)}`}>{titleCase(service.status)}</span>
            </div>
            <p>{service.detail || `${service.events} retained signals in this window.`}</p>
            {['ui', 'backend', 'realtime-api'].includes(service.service) ? <div className="observability-service-version" title={service.service === 'ui' ? 'Version of the UI loaded in this browser' : undefined}>
              <span>Version</span>
              <strong>{typeof service.build?.version === 'string' && service.build.version ? service.build.version : 'Not reported'}</strong>
              {typeof service.build?.revision === 'string' && service.build.revision ? <code title={`Revision ${service.build.revision}`}>{service.build.revision.slice(0, 8)}</code> : null}
            </div> : null}
            <dl className="observability-service-stats">
              <div><dt>Events</dt><dd>{service.events}</dd></div>
              <div><dt>Error rate</dt><dd>{service.errorRate === null ? '—' : `${service.errorRate}%`}</dd></div>
              <div><dt>p95</dt><dd>{formatDuration(service.p95Ms)}</dd></div>
            </dl>
            <small>Last signal: {formatDate(service.lastSeenAt)}</small>
          </article>
          )
        })}
      </div>

      {showAgents && <article className="observability-card">
        <div className="observability-section-heading">
          <div><h3>Model usage</h3><p>Language, embedding, speech, and voice models used during the selected window.</p></div>
          <span>{data?.modelUsage?.calls ?? 0} requests</span>
        </div>
        <p>Requests count model operations with a recorded outcome, not phone calls. Speech recognition sessions count once per session. Rates use all retained requests, including cancellations. SDK retries are part of one request.</p>
        {data?.modelUsage ? (data.modelUsage.models?.length ? <div className="observability-table-wrap">
          <table className="observability-table">
            <thead><tr><th>Model / provider</th><th>Service / type</th><th>Requests</th><th>Input tokens</th><th>Output tokens</th><th>Success</th><th>Errors</th><th>Cancelled</th><th>Last used</th></tr></thead>
            <tbody>{data.modelUsage.models.map(model => <tr key={`${model.provider}:${model.model}:${model.service}:${model.kind}`}>
              <td><strong>{model.model}</strong><div>{titleCase(model.provider)}</div></td>
              <td>{titleCase(model.service)}<div>{titleCase(model.kind)}</div></td>
              <td>{model.calls.toLocaleString()}</td>
              <td>{model.inputTokens === null ? 'Not reported' : model.inputTokens.toLocaleString()}<div><small>{model.inputReported} of {model.calls} requests reported</small></div></td>
              <td>{model.outputTokens === null ? 'Not reported' : model.outputTokens.toLocaleString()}<div><small>{model.outputReported} of {model.calls} requests reported</small></div></td>
              <td>{model.successRate}%<div><small>{model.successes.toLocaleString()} succeeded</small></div></td>
              <td>{model.errorRate}%<div><small>{model.errors.toLocaleString()} failed</small></div></td>
              <td>{model.cancelled.toLocaleString()}</td>
              <td>{formatDate(model.lastUsedAt)}</td>
            </tr>)}</tbody>
          </table>
        </div> : <div className="admin-operations-empty">No model requests recorded in this window. Usage is collected for new requests after instrumentation is deployed.</div>) : <div className="admin-operations-empty">Model usage reporting is not available from the backend yet.</div>}
        <p><small>Totals include all retained model records in this window, beyond the recent-event preview. Successful model requests are not sampled. Missing provider usage is not counted as zero; partial totals show reporting coverage. Collection outages or dropped events can reduce totals.</small></p>
      </article>}

      <div className={`observability-detail-grid ${showHealth && showAgents ? '' : 'single-column'}`} hidden={!(showHealth || showAgents)}>
        <article className="observability-card observability-tools-card" hidden={!showAgents}>
          <div className="observability-section-heading">
            <div><h3>Agent tool usage</h3><p>Sanitized execution spans; arguments and results are excluded.</p></div>
            <span>{data?.tools?.length || 0}</span>
          </div>
          {(data?.tools || []).length ? (
            <div className="observability-table-wrap">
              <table className="observability-table">
                <thead><tr><th>Tool</th><th>Calls</th><th>Success</th><th>Average</th><th>p95</th><th>Last used</th></tr></thead>
                <tbody>{data.tools.map(tool => (
                  <tr key={tool.name}>
                    <td><strong>{tool.name}</strong></td><td>{tool.calls}</td><td>{tool.successRate}%</td>
                    <td>{formatDuration(tool.averageMs)}</td><td>{formatDuration(tool.p95Ms)}</td><td>{formatDate(tool.lastUsedAt)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          ) : <div className="admin-operations-empty">No tool calls retained in this window.</div>}
        </article>

        <article className="observability-card observability-policy-card" hidden={!showHealth}>
          <div className="observability-section-heading"><div><h3>Performance guardrails</h3><p>Collection pressure and sampling policy.</p></div><span className="operations-status healthy">Async</span></div>
          <dl className="observability-policy-list">
            <div><dt>Successful request sampling</dt><dd>{Math.round((collection.successSampleRate || 0) * 100)}%</dd></div>
            <div><dt>Errors sampled</dt><dd>{collection.errorsSampled ? 'Yes' : 'No — retain all'}</dd></div>
            <div><dt>Telemetry queue</dt><dd>{queue.depth || 0} / {queue.capacity || '—'}</dd></div>
            <div><dt>Dropped since start</dt><dd className={queue.droppedSinceStart ? 'danger-text' : ''}>{queue.droppedSinceStart || 0}</dd></div>
            <div><dt>Batch size</dt><dd>{queue.batchSize || '—'}</dd></div>
            <div><dt>Flush interval</dt><dd>{queue.flushIntervalSeconds || '—'} s</dd></div>
          </dl>
        </article>
      </div>

      {showCalls && <CallLatencyPanel data={data?.callLatency} />}

      <article className="observability-card live-logs-card" hidden={!showLogging}>
        <div className="observability-section-heading">
          <div><h3>Live logs</h3><p>Bounded snapshots from backend and realtime files. Refresh on demand; no continuous stream is opened.</p></div>
          <span>{visibleLogs.length}</span>
        </div>
        <form className="live-log-controls" onSubmit={event => { event.preventDefault(); loadLogs() }}>
          <label><span>Service</span><select value={logService} onChange={event => setLogService(event.target.value)}><option value="all">All services</option><option value="backend">Backend</option><option value="realtime-api">Realtime API</option></select></label>
          <label><span>Level</span><select value={logLevel} onChange={event => setLogLevel(event.target.value)}><option value="all">All levels</option><option value="debug">Debug</option><option value="info">Info</option><option value="warning">Warning</option><option value="error">Error</option><option value="critical">Critical</option></select></label>
          <label className="live-log-search"><span>Search</span><input value={logSearch} onChange={event => setLogSearch(event.target.value)} placeholder="Message, logger, trace, run…" /></label>
          <label><span>Load</span><select value={logLimit} onChange={event => setLogLimit(Number(event.target.value))}><option value="100">100</option><option value="200">200</option><option value="500">500</option></select></label>
          <button type="submit" className="admin-refresh-button" disabled={logsLoading}><BusyContent busy={logsLoading} label={"Loading…"}>{'Load logs'}</BusyContent></button>
        </form>
        <div className="live-log-sources" aria-label="Log source status">
          {Object.entries(logSources).map(([service, source]) => <span key={service} className={`operations-status ${source.status === 'ready' ? 'connected' : 'failed'}`}>{titleCase(service)}: {titleCase(source.status)} · {source.count || 0}</span>)}
        </div>
        {logsError ? <div className="admin-operations-alert" role="alert">{logsError}</div> : null}
        {visibleLogs.length ? <div className="live-log-list">
          {visibleLogs.map((log, index) => {
            const logId = `${log.service || 'service'}:${log.id || index}`
            const expanded = expandedLog === logId
            return <div className={`live-log-entry ${expanded ? 'expanded' : ''}`} key={logId}>
              <button type="button" className="live-log-row" onClick={() => setExpandedLog(expanded ? null : logId)} aria-expanded={expanded}>
                <time>{formatDate(log.timestamp)}</time>
                <span className={`live-log-level ${String(log.level || 'unknown').toLowerCase()}`}>{titleCase(log.level)}</span>
                <span className="live-log-service">{titleCase(log.service)}</span>
                <span className="live-log-message">{log.message || 'No message'}</span>
                <span className="live-log-toggle">{expanded ? 'Hide' : 'View'}</span>
              </button>
              {expanded ? <div className="live-log-detail">
                <div><h4>Full message</h4><pre>{log.message || 'No message'}</pre></div>
                {log.stackTrace ? <div><h4>Stack trace</h4><pre data-testid="live-log-stack">{log.stackTrace}</pre></div> : null}
                <div><h4>Complete structured entry</h4><pre>{JSON.stringify(log, null, 2)}</pre></div>
              </div> : null}
            </div>
          })}
        </div> : <div className="admin-operations-empty">{logsLoading ? <LoadingState label="Loading recent service logs…" /> : logsLoaded ? 'No logs match these filters.' : 'Open the Logs tab to load recent service output.'}</div>}
      </article>

      <article className="observability-card" hidden={!showLogging}>
        <div className="observability-section-heading">
          <div><h3>Logging health</h3><p>Structured logs are queued and locally bounded; telemetry never waits for log delivery.</p></div>
          <span>{data?.logging?.length || 0}</span>
        </div>
        <div className="observability-log-grid">
          {(data?.logging || []).map(policy => (
            <div className="observability-log-policy" key={policy.service}>
              <div><strong>{titleCase(policy.service)}</strong><span className="operations-status healthy">{titleCase(policy.delivery)}</span></div>
              <dl>
                <div><dt>Format</dt><dd>{titleCase(policy.format)}</dd></div>
                <div><dt>Rotation</dt><dd>{formatBytes(policy.rotation?.maxBytes)} × {policy.rotation?.backupFiles} backups</dd></div>
                <div><dt>Queue</dt><dd>{policy.queueCapacity} entries</dd></div>
                <div><dt>Dropped</dt><dd>{policy.droppedSinceStart || 0}</dd></div>
              </dl>
            </div>
          ))}
        </div>
      </article>

      <article className="observability-card" hidden={!showLogging}>
        <div className="observability-section-heading observability-events-heading">
          <div><h3>Recent correlated events</h3><p>Search by service, tool, trace, run, or session identifier.</p></div>
          <input aria-label="Search observability events" value={search} onChange={event => setSearch(event.target.value)} placeholder="Search traces and events" />
        </div>
        {filteredEvents.length ? (
          <div className="observability-event-list">
            {filteredEvents.map(event => (
              <div className="observability-event-row" key={`${event.id}-${event.timestamp}`}>
                <span className={`service-dot`} data-status={statusClass(event.status)} />
                <div>
                  <strong>{event.toolName || event.name}</strong>
                  {eventContext(event).length ? (
                    <small className="observability-event-context" aria-label="Event context">
                      {eventContext(event).map(([key, value]) => (
                        <span key={key}>{titleCase(key)}: {String(value)}</span>
                      ))}
                    </small>
                  ) : null}
                  <small>{titleCase(event.service)} · {titleCase(event.eventType)} · {event.traceId ? `Trace ${event.traceId}` : 'No trace ID'}</small>
                </div>
                <span className={`operations-status ${statusClass(event.status)}`}>{titleCase(event.status)}</span>
                <span>{formatDuration(event.durationMs)}</span>
                <time>{formatDate(event.timestamp)}</time>
              </div>
            ))}
          </div>
        ) : <div className="admin-operations-empty">No matching retained events.</div>}
      </article>
    </section>
  )
}

export default ObservabilityCenter
